<template>
  <Navigation with-exit @back="send('BACK')" @exit="send('EXIT')" />
  <Title>{{ i18n(translations.title) }}</Title>

  <div class="space-y-12">
    <slot name="before-form" />

    <RevForm :initial-values :validate="validate()" @submit="handleSubmit">
      <template #default="{ values, errors }">
        <div class="space-y-24">
          <RevInputText
            id="refundAmount"
            v-model="values.refundAmount"
            :disabled="true"
            :error="errors.refundAmount"
            :format="(value) => formatAmountWithoutSymbol(value as string)"
            :label="i18n(translations.inputAmount)"
          />
          <RevInputSelect
            id="partialRefundReason"
            v-model="values.partialRefundReason"
            :error="errors.partialRefundReason"
            :label="i18n(translations.reasonSelect)"
            :options="reasonOptions"
          />
          <RevInputText
            v-if="isOtherReasonSelected(values)"
            id="partialRefundMessage"
            v-model="values.partialRefundMessage"
            :error="errors.partialRefundMessage"
            :label="i18n(translations.messageInput)"
          />

          <div class="mt-24 flex items-center gap-16">
            <slot />
            <RevButton
              :disabled="isLoading"
              :loading="isLoading"
              type="submit"
              variant="primary"
            >
              {{ i18n(translations.refundButton) }}
            </RevButton>
          </div>
        </div>
      </template>
    </RevForm>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref } from 'vue'

import type { MonetaryAmount } from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { sleep } from '@backmarket/utils/async/sleep'
import { RevButton } from '@ds/components/Button'
import { RevForm, makeValidate, maxLength, required } from '@ds/components/Form'
import { RevInputSelect } from '@ds/components/InputSelect'
import { RevInputText } from '@ds/components/InputText'

import type { PartialRefundReason } from '~/api/care-folders/types/common'
import { useOrderlinePublicIdFromRoute } from '~/scopes/customer-care/pages/request/composables/useOrderlinePublicIdFromRoute'

import type { MachineSend } from '../../../ActionBar.machine.type'
import Navigation from '../../Navigation/Navigation.vue'
import Title from '../../Title/Title.vue'
import { requiredIf } from '../RefundFlow.utils'

import translations from './PartialRefundStep.translations'

const i18n = useI18n()
const logger = useLogger()
const { openErrorToast, openSuccessToast } = useTheToast()
const $httpFetch = useHttpFetch()
const orderlinePublicId = useOrderlinePublicIdFromRoute()

const emit = defineEmits(['done'])

const { send, price } = defineProps<{
  price: MonetaryAmount
  send: MachineSend
}>()

const EXPLANATION_MAX_LENGTH = 40

const formatAmountWithoutSymbol = (amount: string) => {
  return i18n.price(amount, { symbol: false })
}

const isLoading = ref(false)

type Values = {
  refundAmount: string
  partialRefundReason: string
  partialRefundMessage: string
}

const initialValues = reactive<Values>({
  refundAmount: price.amount || '',
  partialRefundReason: '',
  partialRefundMessage: '',
})

const validate = () => {
  return makeValidate<Values>({
    refundAmount: [required(i18n(translations.isRequired))],
    partialRefundReason: [required(i18n(translations.isRequired))],
    partialRefundMessage: [
      requiredIf<Values>(
        'partialRefundReason',
        'OTHER',
        i18n(translations.isRequired),
      ),
      maxLength(
        EXPLANATION_MAX_LENGTH,
        i18n(translations.characterLimitErrorMessage),
      ),
    ],
  })
}

const reasonOptions: {
  label: string
  value: PartialRefundReason
}[] = [
  {
    label: i18n(translations.accessoryProblemReason),
    value: 'ACCESSORY_PROBLEM',
  },
  {
    label: i18n(translations.returnLabelFeesReason),
    value: 'RETURN_LABEL_FEES',
  },
  {
    label: i18n(translations.thirdPartyRepairReason),
    value: 'THIRD_PARTY_REPAIR',
  },
  {
    label: i18n(translations.appearanceIssueReason),
    value: 'APPEARANCE_ISSUE',
  },
  {
    label: i18n(translations.performanceIssueReason),
    value: 'PERFORMANCE_ISSUE',
  },
  {
    label: i18n(translations.otherReason),
    value: 'OTHER',
  },
]

const isOtherReasonSelected = (values: Values) =>
  values.partialRefundReason === 'OTHER'

const handleSubmit = async (values: Values) => {
  isLoading.value = true

  const { refundAmount, partialRefundReason, partialRefundMessage } = values
  const formattedAmount = refundAmount.replace(',', '.')

  try {
    await $httpFetch(
      '/bm/customer_request/orderlines/:orderlinePublicId/refund',
      {
        method: 'POST',
        pathParams: {
          orderlinePublicId: orderlinePublicId.value,
        },
        body: {
          monetaryAmount: {
            amount: formattedAmount,
            currency: price.currency,
          },
          partialRefundReason,
          ...(partialRefundReason === 'OTHER' && { partialRefundMessage }),
        },
      },
    )
    await sleep(1000)

    openSuccessToast()

    emit('done')
  } catch (err) {
    openErrorToast({
      title: i18n(translations.errorToastTitle),
      content: i18n(translations.errorToastLabel),
    })

    const error = err as Error
    logger.error(error.message, {
      error,
      owners: ['bot-squad-seller-care-front'],
    })
  } finally {
    isLoading.value = false
  }
}
</script>
